import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Navbar } from './components/Navbar';
import { HomePage } from './pages/HomePage';
import { BrainstormingPage } from './pages/BrainstormingPage';
import { ABCMethodPage } from './pages/ABCMethodPage';
import { DatenschutzPage } from './pages/DatenschutzPage';
import { ImpressumPage } from './pages/ImpressumPage';
import { ResultPage } from './pages/ResultPage';
import { JoinPage } from './pages/JoinPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { checkSupabaseConnection, reconnectRealtime } from './lib/supabase';
import { LanguageProvider } from './lib/i18n/context';
import { DEFAULT_LANGUAGE, isValidLanguage } from './lib/i18n/config';
import useMatomoTracking from './hooks/useMatomoTracking';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

function AppContent() {
  useMatomoTracking();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50">
      <Helmet>
        <meta name="description" content="Use the ABC method for creative brainstorming. Collaborate in real-time with up to 5 people and get AI analysis of your ideas." />
        <link rel="canonical" href="https://abcbrain.xyz" />
      </Helmet>
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <Routes>
          {/* Redirect root to language-specific home based on browser language */}
          <Route path="/" element={<Navigate to={`/${getBrowserLanguage()}`} replace />} />
          
          {/* Direct routes (without language prefix) for easier sharing */}
          <Route path="/result/:token" element={<ResultPage />} />
          <Route path="/session/:sessionId" element={<BrainstormingPage />} />
          <Route path="/join/:token" element={<JoinPage />} />
          
          {/* Language-specific routes */}
          <Route path="/:lang">
            <Route index element={<HomePage />} />
            <Route path="session/:sessionId" element={<BrainstormingPage />} />
            <Route path="result/:token" element={<ResultPage />} />
            <Route path="join/:token" element={<JoinPage />} />
            {/* Use canonical (English) slugs in routes */}
            <Route path="abc-method" element={<ABCMethodPage />} />
            <Route path="die-ABC-Listen-Methode" element={<ABCMethodPage />} />
            <Route path="privacy" element={<DatenschutzPage />} />
            <Route path="datenschutz" element={<DatenschutzPage />} />
            <Route path="imprint" element={<ImpressumPage />} />
            <Route path="impressum" element={<ImpressumPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </main>
    </div>
  );
}

// Helper function to get browser language or default
function getBrowserLanguage(): string {
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  return isValidLanguage(browserLang) ? browserLang : DEFAULT_LANGUAGE;
}

function App() {
  const [connectionError, setConnectionError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const { t } = useTranslation();

  const checkConnection = useCallback(async () => {
    const isConnected = await checkSupabaseConnection();
    setConnectionError(!isConnected);
    return isConnected;
  }, []);

  useEffect(() => {
    checkConnection();

    // Set up periodic connection checks
    const intervalId = setInterval(async () => {
      const isConnected = await checkConnection();
      if (isConnected) {
        // If connection is restored, try to reconnect realtime
        await reconnectRealtime();
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId);
  }, [checkConnection]);

  const handleRetry = async () => {
    setIsRetrying(true);
    const isConnected = await checkConnection();
    if (isConnected) {
      await reconnectRealtime();
    }
    setIsRetrying(false);
  };

  if (connectionError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-6 shadow-lg max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {t('error.connection.title')}
          </h1>
          <p className="text-gray-600 mb-4">
            {t('error.connection.message')}
          </p>
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
          >
            {isRetrying ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('error.connection.connecting')}</span>
              </>
            ) : (
              <span>{t('error.connection.retry')}</span>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </Router>
  );
}

export default App;